import React, { useEffect, useState } from "react";
import FormActionButton from "../shared-components/FormActionButton";
import {
	ADDRESS_ENDPOINT,
	ADDRESS_RETRIEVE,
	CLIENT_DETAILS,
	claimValue,
	formPages,
	VALIDATE_EMAIL,
} from "../../constants";
import ListRadioOptions from "../shared-components/ListRadioOptions";
import { detectBrand } from "../../utils/brandDetection";
import { mapVerifiedClientDetails } from "../../utils/verifiedClientDetails";


export default function IntroCheckDetails({
	compensate,
	onNext,
	onBack,
	isSubmitted,
}) {
	const [correctDetails, setCorrectDetails] = useState(
		compensate.correctDetails,
	);
	const [correctDetailsError, setCorrectDetailsError] = useState(false);
	const [clientDetails, setClientDetails] = useState([]);

	// Helper function for case-insensitive URL parameter retrieval
	const getUrlParam = (paramName) => {
		const urlParams = new URLSearchParams(window.location.search);
		const key = Array.from(urlParams.keys()).find(
			(k) => k.toLowerCase() === paramName.toLowerCase(),
		);
		return key ? urlParams.get(key) : null;
	};

	// Case-insensitive retrieval of all UTM parameters
	const normalizeMarketingChannel = (value) => {
		const trimmed = value?.trim();
		if (!trimmed) return trimmed;
		const lower = trimmed.toLowerCase();
		if (lower === "email") return "Email";
		if (lower === "sms") return "SMS";
		return trimmed;
	};
	const marketingChannel = normalizeMarketingChannel(getUrlParam("utm_source"));
	const isEmailJourney = marketingChannel?.trim().toLowerCase() === "email";
	const marketingCampaignName = getUrlParam("utm_campaign");
	const marketingAudience = getUrlParam("utm_term");
	const marketingCreative = getUrlParam("utm_content");
	const marketingCustomerReferenceNumber = getUrlParam("utm_crn");
	const brand = detectBrand();
	const marketingAcquisitionBrand = brand.name;
	const uid = getUrlParam("utm_id");
	const uniqueID = uid ? uid : "";

	const averageClaimValue = claimValue * 3.5;

	useEffect(() => {
		const fetchClientDetails = async () => {
			isSubmitted(true);

			if (uid === "address") {
				onNext({}, formPages.AddressLookup);
				isSubmitted(false);
			} else if (uid) {
				try {
					const response = await CLIENT_DETAILS(uid, marketingChannel);
					setClientDetails(response);
					const isEmptyResponse =
						!response || !Object.keys(response).length || !response.uid;
					if (isEmptyResponse) {
						isSubmitted(false);
						onNext(
							{
								uniqueID: uid,
								claims: [],
								marketingChannel,
								marketingCampaignName,
								marketingAudience,
								marketingCreative,
								marketingCustomerReferenceNumber,
								marketingAcquisitionBrand,
							},
							formPages.NewClaimantDetails,
						);
						return;
					}

					if (response?.kyc_required) {
						const mapped = mapVerifiedClientDetails(response);
						onNext(
							{
								...mapped,
								uniqueID: uid,
								claims: [],
								marketingChannel,
								marketingCampaignName,
								marketingAudience,
								marketingCreative,
								marketingCustomerReferenceNumber,
								marketingAcquisitionBrand,
								surnameVerificationRequired: !isEmailJourney,
								signature: undefined,
								signatureToken: isEmailJourney ? mapped.signatureToken : null,
							},
							formPages.ConfirmDetails,
						);
						isSubmitted(false);
						return;
					}

					// Non-KYC-gated match: skip the confirm-details screen, go
					// straight to NewClaimantDetails prefilled (page redundant for this case).
					const emailValidation = !response.email
						? { isValid: false, domainDoesNotExist: false }
						: await VALIDATE_EMAIL(response?.email);
					const emailToPass = emailValidation.domainDoesNotExist
						? ""
						: response?.email;
					onNext(
						{
							title: response?.title,
							firstName: response?.first_name,
							surname: response?.last_name,
							dob: response?.date_of_birth,
							mobile: response?.telephone,
							email: emailToPass,
							uniqueID: uid ? uid : "",
							claims: [],
							marketingChannel,
							marketingCampaignName,
							marketingAudience,
							marketingCreative,
							marketingCustomerReferenceNumber,
							marketingAcquisitionBrand,
						},
						formPages.NewClaimantDetails,
					);
					isSubmitted(false);
				} catch (error) {
					console.error("Error fetching client details:", error);
					isSubmitted(false);
					onNext(
						{
							uniqueID: uid,
							claims: [],
							marketingChannel,
							marketingCampaignName,
							marketingAudience,
							marketingCreative,
							marketingCustomerReferenceNumber,
							marketingAcquisitionBrand,
						},
						formPages.NewClaimantDetails,
					);
				}
			} else {
				isSubmitted(false);
				onNext(
					{
						claims: [],
						marketingChannel,
						marketingCampaignName,
						marketingAudience,
						marketingCreative,
						marketingCustomerReferenceNumber,
						marketingAcquisitionBrand,
						uniqueID,
					},
					formPages.NewClaimantDetails,
				);
			}
		};
		fetchClientDetails();
	}, []);

	const directAddressSearch = async () => {
		if (clientDetails.address) {
			const fullAddress =
				clientDetails.address.address_line_1 +
				" " +
				clientDetails.address?.address_line_2 +
				" " +
				clientDetails.address?.address_city +
				" " +
				clientDetails.address.address_postcode;
			try {
				const addressid = await ADDRESS_ENDPOINT(compensate, fullAddress.trim());
				const loqateAddress = await ADDRESS_RETRIEVE(addressid[0].Id);
				// console.log(loqateAddress);
				if (!loqateAddress?.Error && correctDetails === true) {
					onNext(
						{
							correctDetails,
							title: clientDetails?.title,
							firstName: clientDetails?.first_name,
							surname: clientDetails?.last_name,
							dob: clientDetails?.date_of_birth,
							mobile: clientDetails?.telephone,
							email: clientDetails?.email,
							uid: clientDetails?.uid,
							currentAddress: loqateAddress,
							claims: [],
							marketingChannel,
							marketingCampaignName,
							marketingAudience,
							marketingCreative,
							marketingCustomerReferenceNumber,
							marketingAcquisitionBrand,
						},
						formPages.RegistrationDetails,
					);
				} else {
					onNext(
						{
							correctDetails,
							title: clientDetails?.title,
							firstName: clientDetails?.first_name,
							surname: clientDetails?.last_name,
							dob: clientDetails?.date_of_birth,
							mobile: clientDetails?.telephone,
							email: clientDetails?.email,
							uid: clientDetails?.uid,
							marketingChannel,
							marketingCampaignName,
							marketingAudience,
							marketingCreative,
							marketingCustomerReferenceNumber,
							marketingAcquisitionBrand,
						},
						formPages.NewClaimantDetails,
					);
				}
			} catch (error) {
				console.error("Error:", error);
			}
		}
	};
	return (
		<>
			<div className="intro-check-loading-space" aria-hidden="true" />
			<div hidden aria-hidden="true">
				<div
					className="d-flex justify-content-center align-items-center heading-icon fdc-form"
					data-testid="page-intro-check"
				>
					<img src="/icons/badge-pound-sterling.svg" alt="icon1" />
					<h2 className="title">
						You could be owed an average of{" "}
						<span className="ycm-orange">
							£{averageClaimValue.toLocaleString()}*
						</span>{" "}
						based on 3.5 cars per household for mis-sold car finance. Check for
						FREE in 60 seconds.
					</h2>
				</div>
				<div className="fdc-form gif-input">
					<div className="row">
						<div className="col-md-12">
							<div className="fw-semibold theme-color-1">
								As a valued customer, we want to make sure your information we
								hold is correct. Please take a moment to review the details below:
							</div>
						</div>
						<div className="col-md-12">
							<div className="box-message fw-semibold theme-color-1">
								<p>
									{clientDetails?.title} {clientDetails?.first_name}{" "}
									{clientDetails?.middle_name && `${clientDetails?.middle_name} `}
									{clientDetails?.last_name}{" "}
								</p>
								{clientDetails?.address?.address_line_1 && (
									<p className="mb-0">{clientDetails?.address?.address_line_1}</p>
								)}
								{clientDetails?.address?.address_line_2 && (
									<p className="mb-0">{clientDetails?.address?.address_line_2}</p>
								)}
								{clientDetails?.address?.address_line_3 && (
									<p className="mb-0">{clientDetails?.address?.address_line_3}</p>
								)}
								{clientDetails?.address?.address_city && (
									<p className="mb-0">{clientDetails?.address?.address_city}</p>
								)}
								{clientDetails?.address?.address_postcode && (
									<p className="mb-0">
										{clientDetails?.address?.address_postcode}
									</p>
								)}
								<p className="mt-3">
									{clientDetails?.date_of_birth && "Date of Birth:"}{" "}
									{clientDetails?.date_of_birth}
								</p>
								<p>
									{clientDetails?.telephone && "Telephone:"}{" "}
									{clientDetails?.telephone}
								</p>
								<p>
									{clientDetails?.email && "Email:"} {clientDetails?.email}
								</p>
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-block mb-0">
								<ListRadioOptions
									label={"Are these details correct?"}
									items={[
										{ text: "Yes", value: true },
										{ text: "No", value: false },
									]}
									onSelect={(val) => {
										setCorrectDetails(val);
										setCorrectDetailsError(false);
									}}
									value={correctDetails}
									className="d-inline-block"
									divStyle="full-width"
									testId="intro-correct-details"
								/>
								{correctDetailsError ? (
									<div className="error-message">
										<p>Please select an option</p>
									</div>
								) : (
									<></>
								)}
							</div>
						</div>
						<div className="col-md-12">
							<div className="fw-semibold text-center theme-color-1">
								We'll check your claim for free
							</div>
						</div>
						<div className="col-md-6 text-center mx-auto">
							<FormActionButton
								onNext={() => {
									if (correctDetails !== true && correctDetails !== false) {
										setCorrectDetailsError(true);
									}
									if (correctDetails === true || correctDetails === false) {
										directAddressSearch();
									}
								}}
								nextTestId="intro-btn-next"
							/>
							<br />
							<img
								className="light-bulb mb-2"
								src="/icons/Lightbulb-Tip.png"
								alt="light bulb"
							/>
							<b className="tips">Claim with confidence with Claims1</b>
							<br />
							<p className="terms">
								*based on an average refund of hidden commissions of £1,100, plus
								8% annual compensatory interest, and an average of 3.5 cars per
								household.
							</p>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
